/**
 * Customers data for the web screens: React Query over the shared API client
 * (packages/shared/src/customers), bound to webApiClient. Keys carry the store,
 * since what a store sees depends on it (its own customers plus shared ones).
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  archiveCustomer,
  createCustomer,
  CUSTOMERS_FEATURE,
  getCustomer,
  getWorkOrderCustomer,
  listCustomerJobs,
  listCustomers,
  listCustomerVehicles,
  restoreCustomer,
  setWorkOrderCustomer,
  updateCustomer,
  type Customer,
  type CustomerInput,
  type CustomerJobsQuery,
  type CustomerListQuery,
  type CustomerVehiclesQuery,
} from '@turn-connect/shared';
import { webApiClient } from '../../services/apiClient';
import { useAccountFeature } from '../../hooks/useAccountFeature';

export const customerKeys = {
  all: ['customers'] as const,
  list: (locationId: string, q: CustomerListQuery) => ['customers', 'list', locationId, q] as const,
  lists: (locationId: string) => ['customers', 'list', locationId] as const,
  detail: (locationId: string, id: string) => ['customers', 'detail', locationId, id] as const,
  jobs: (locationId: string, id: string, q: CustomerJobsQuery) => ['customers', 'jobs', locationId, id, q] as const,
  vehicles: (locationId: string, id: string, q: CustomerVehiclesQuery) => ['customers', 'vehicles', locationId, id, q] as const,
  ofWorkOrder: (workOrderId: string) => ['customers', 'work-order', workOrderId] as const,
};

/** Whether the account has the Customers module on (false until known). */
export function useCustomersOn(accountId: string | null | undefined, options: { enabled?: boolean } = {}) {
  return useAccountFeature(accountId, CUSTOMERS_FEATURE, options);
}

export function useCustomerList(locationId: string | null | undefined, query: CustomerListQuery, enabled = true) {
  return useQuery({
    queryKey: customerKeys.list(locationId ?? '', query),
    queryFn: () => listCustomers(webApiClient, locationId as string, query),
    enabled: enabled && !!locationId,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

export function useCustomer(locationId: string | null | undefined, customerId: string | null | undefined) {
  return useQuery({
    queryKey: customerKeys.detail(locationId ?? '', customerId ?? ''),
    queryFn: () => getCustomer(webApiClient, locationId as string, customerId as string),
    enabled: !!locationId && !!customerId,
  });
}

export function useCustomerJobs(locationId: string, customerId: string, query: CustomerJobsQuery) {
  return useQuery({
    queryKey: customerKeys.jobs(locationId, customerId, query),
    queryFn: () => listCustomerJobs(webApiClient, locationId, customerId, query),
    enabled: !!locationId && !!customerId,
    placeholderData: keepPreviousData,
  });
}

/** A customer's vehicles: searched, sorted, and every one or a page (limit). */
export function useCustomerVehicles(
  locationId: string | null | undefined,
  customerId: string | null | undefined,
  query: CustomerVehiclesQuery = {}
) {
  return useQuery({
    queryKey: customerKeys.vehicles(locationId ?? '', customerId ?? '', query),
    queryFn: () => listCustomerVehicles(webApiClient, locationId as string, customerId as string, query),
    enabled: !!locationId && !!customerId,
    placeholderData: keepPreviousData,
  });
}

export function useWorkOrderCustomer(workOrderId: string, enabled: boolean) {
  return useQuery({
    queryKey: customerKeys.ofWorkOrder(workOrderId),
    queryFn: () => getWorkOrderCustomer(webApiClient, workOrderId),
    enabled: enabled && !!workOrderId,
    retry: false,
  });
}

/** Add, edit, archive and restore — each refreshes the store's lists and the customer. */
export function useCustomerMutations(locationId: string) {
  const qc = useQueryClient();
  const refresh = (c?: Customer) => {
    void qc.invalidateQueries({ queryKey: customerKeys.all });
    if (c) qc.setQueryData(customerKeys.detail(locationId, c.id), c);
  };
  const create = useMutation({
    mutationFn: (input: CustomerInput) => createCustomer(webApiClient, locationId, input),
    onSuccess: refresh,
  });
  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: CustomerInput }) => updateCustomer(webApiClient, locationId, id, input),
    onSuccess: refresh,
  });
  const archive = useMutation({
    mutationFn: (id: string) => archiveCustomer(webApiClient, locationId, id),
    onSuccess: refresh,
  });
  const restore = useMutation({
    mutationFn: (id: string) => restoreCustomer(webApiClient, locationId, id),
    onSuccess: refresh,
  });
  return { create, update, archive, restore };
}

export function useSetWorkOrderCustomer(workOrderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (customerId: string | null) => setWorkOrderCustomer(webApiClient, workOrderId, customerId),
    onSuccess: (customer) => {
      qc.setQueryData(customerKeys.ofWorkOrder(workOrderId), customer);
      void qc.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}
